// getSession-wedge self-heal — PR #173 follow-up.
//
// 2026-07-02 UPDATE (idle-wake root-cause audit,
// docs/audits/IDLE-WAKE-DEADLOCK-ROOTCAUSE-2026-07-02.md): the lock this
// watchdog was built to fight is GONE at the source. `lock: noLock` in
// supabase.ts was what opted the client into auth-js's legacy in-process lock
// path — on 2.108.1 omitting `lock` gives lockless single-flight coordination
// — and the two subscribers that called getSession() inside onAuthStateChange
// (the circular wait that made the wedge PERMANENT on every token refresh)
// now use the callback's session argument. This watchdog stays installed as
// the LAST-RESORT backstop: its probes key off getSession latency, not the
// lock's internals, so they remain valid for any future wedge whatever the
// mechanism. The docblock below describes the original lock-era mechanics.
//
// ORIGINAL ROOT CAUSE (confirmed from a live production repro + a React-Query
// cache dump, cross-checked against the bundled @supabase/auth-js 2.108.1
// source): supabase-js resolves EVERY request's bearer via
// `await auth.getSession()` BEFORE it builds the fetch. auth-js serialised
// token reads behind an IN-PROCESS lock (GoTrueClient `lockAcquired` /
// `pendingInLock`) that is SEPARATE from navigator.locks (which the old
// `lock: noLock` neutralised) and is NOT covered by the anon-guard or
// sessionWatchdog (both read localStorage directly). When the visibilitychange
// recover held that lock and a cold detail route mounted a burst of ~10
// queries at once, every getSession() queued behind the lock; the queue
// deadlocked, NO query ever issued its fetch, the page froze on a loading
// spinner forever, and only a manual reload recovered it — a fresh JS context
// holds no in-process lock. (The earlier `d0647b57` de-await fixed a
// React-Query-level cycle one layer ABOVE this lock, so it couldn't touch it.)
//
// We could not un-wedge that lock from inside the same JS context — any
// getSession()/setSession() we call just queues behind it too. So this
// watchdog automates the one thing that DOES recover it: a guarded reload. The
// hard part is doing that WITHOUT reloading over auth-js's OWN legitimate ~30s
// bounded refresh, which holds the client busy transiently even when healthy.
//
// Every guard below exists because an adversarial review flagged the exact
// failure it prevents:
//   - BOOT exclusion (`everResolved`): a slow cold-boot getSession is slow-start,
//     not a wedge, and a fresh context can't be lock-wedged. We only treat a
//     timeout as a wedge AFTER getSession has resolved at least once this
//     page-session. Seeded by an immediate probe at install so a healthy boot
//     establishes it right away (not only after the first 5s poll).
//   - PERSISTENCE (two-tier): never reload on one probe timeout. auth-js holds the
//     same lock up to ~30s (AUTO_REFRESH_TICK_DURATION_MS) on a real refresh, and
//     our watchdog's `refreshInFlight` does NOT observe auth-js's lock — BUT auth-js
//     only refreshes (and thus only long-holds the lock for) a token within its 90s
//     EXPIRY_MARGIN. So we read the stored token's expiry: if it's comfortably valid
//     (> FRESH_MARGIN_S from expiry) a long lock-hold CAN'T be a legit refresh
//     (auth-js no-ops a fresh token) → confirm fast (FAST_CONFIRM_MS); if it's near
//     expiry, stay conservative past the ~30s ceiling (SLOW_CONFIRM_MS). The common
//     idle-return repro lands on the FAST path (the token was just refreshed to ~1h).
//     Either way require >= 2 probes; PROBE_MS (6s) is above a merely-slow
//     (memory-pressured) getSession so slowness isn't mistaken for a wedge.
//   - Never reload offline (a reload can't re-fetch) or with no valid stored token
//     (let App route to /login — never sign out on a navigator.onLine hint, which
//     lies on weak links / captive portals).
//   - KIOSK exemption: pre-auth device-token routes (/clock-face) run on the anon
//     key and NEVER read the authed session, so a wedged getSession CAN'T break
//     them — a reload would only interrupt an in-flight scan / offline-queue drain
//     for nothing (and spam client_errors with info-level self-heals). Skip the
//     whole watchdog there; if the operator leaves the kiosk for the authed app,
//     check() is back on a normal route and heals the still-wedged lock then.
//   - TERMINAL loop-guard: a COUNT of recent reloads (sessionStorage, distinct
//     from the chunk-reload key). At most MAX_RELOADS within RELOAD_WINDOW_MS;
//     the next confirmed wedge stops permanently — converges even for a wedge that
//     recurs slowly, so the operator is never reloaded indefinitely.
//   - Telemetry that SURVIVES the wedge: reportError() itself awaits the wedged
//     getSession, so we can't call it inline. We queue a breadcrumb to localStorage
//     (synchronous, lock-free) + console, then flush it on the NEXT boot via the
//     healed client. A kill switch (`kf49_wedge_heal` === 'off') disables the whole
//     watchdog without a deploy.

import { supabase, usableStoredToken } from './supabase'
import { readStoredSession, decodeStoredClaims } from './offlineSession'
import { reportError } from './errorReporter'

const POLL_MS          = 4_000
const PROBE_MS         = 6_000   // > any healthy (even memory-pressured) getSession; < the wedge
const FAST_CONFIRM_MS  = 10_000  // token comfortably valid → a long lock-hold can't be a legit refresh
const SLOW_CONFIRM_MS  = 45_000  // token near expiry → must clear auth-js's ~30s bounded-recovery ceiling
const FRESH_MARGIN_S   = 120     // > auth-js's 90s EXPIRY_MARGIN: above this, auth-js won't refresh
const RELOAD_WINDOW_MS = 15 * 60_000
const MAX_RELOADS      = 2        // reloads allowed per window before giving up (terminal)
const RELOADS_KEY      = 'kf49_wedge_reloads'   // sessionStorage; distinct from kf49_chunk_reload_at
const CRUMB_KEY        = 'kf49_wedge_crumbs'     // localStorage; flushed on next boot
const KILL_KEY         = 'kf49_wedge_heal'
// Pre-auth kiosk routes that run on the anon key and never touch the authed
// session (device-token flows). A wedged getSession can't break them, so the
// watchdog no-ops here — see the KIOSK exemption in the docblock above.
const KIOSK_PREFIXES   = ['/clock-face']

let everResolved = false                          // has getSession resolved this page-session? (excludes boot)
let wedgeStart   = 0                              // start of the current continuous wedge (0 = not wedged)
let timeouts     = 0                              // consecutive probe timeouts in the current wedge
let inFlightProbe: Promise<boolean> | null = null // single-flight probe (within one PROBE_MS window)
let stopped      = false                          // terminal: reloads aren't helping → stop probing

function killed(): boolean {
  try { return localStorage.getItem(KILL_KEY) === 'off' } catch { return false }
}

// True on a pre-auth kiosk route (see KIOSK_PREFIXES) — the watchdog skips these
// entirely: they don't depend on the authed session, so a reload is pure downside.
function onSessionlessKiosk(): boolean {
  try { return KIOSK_PREFIXES.some(p => location.pathname.startsWith(p)) } catch { return false }
}

// Breadcrumb that survives the wedge + the reload. reportError() can't run now (it
// awaits the wedged getSession), so persist synchronously and flush next boot.
function queueCrumb(crumb: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.warn('[wedge-heal]', JSON.stringify(crumb))
  try {
    const arr = JSON.parse(localStorage.getItem(CRUMB_KEY) || '[]') as unknown[]
    arr.push({ ...crumb, at: Date.now() })
    localStorage.setItem(CRUMB_KEY, JSON.stringify(arr.slice(-10)))
  } catch { /* best effort */ }
}

// On the NEXT boot (fresh context, lock healed) ship any queued breadcrumbs via
// reportError — this is where the wedge telemetry actually reaches client_errors /
// Sentry. A legitimate self-heal reload is EXPECTED, not an error: send it at 'info'
// so it stays queryable + storm-visible without paging the team. The terminal
// "gave up after MAX_RELOADS" case is rarer + actionable → 'warning'.
// fallow-ignore-next-line complexity  (read/parse/loop glue; high CRAP is 0% coverage on a new module, not branching)
function flushCrumbs(): void {
  let arr: Array<Record<string, unknown>> = []
  try {
    arr = JSON.parse(localStorage.getItem(CRUMB_KEY) || '[]') as Array<Record<string, unknown>>
    localStorage.removeItem(CRUMB_KEY)
  } catch { return }
  for (const c of arr) {
    const level = c.kind === 'getsession-wedge-terminal' ? 'warning' : 'info'
    void reportError(new Error(`getSession wedge self-heal: ${String(c.kind ?? 'event')}`), { source: 'manual', level, context: c })
  }
}

// Race getSession() against PROBE_MS. Returns true if it did NOT resolve in time
// (wedged). Single-flight within a window so a tick can't stack a second probe
// while one is pending. (A truly-wedged getSession promise stays pending forever;
// the next window starts a fresh race — those orphaned getSession calls flush
// harmlessly when the lock releases or are GC'd on reload.)
function probeWedged(): Promise<boolean> {
  if (inFlightProbe) return inFlightProbe
  inFlightProbe = (async () => {
    let resolved = false
    const probe = supabase.auth.getSession()
      .then(() => { resolved = true })
      .catch(() => { resolved = true })   // a THROWN getSession still proves the lock isn't wedged
    await Promise.race([probe, new Promise<void>(r => setTimeout(r, PROBE_MS))])
    if (resolved) everResolved = true
    return !resolved
  })().finally(() => { inFlightProbe = null })
  return inFlightProbe
}

function resetWedge(): void { wedgeStart = 0; timeouts = 0 }

// ── Pure-DOM surfaces + raw-fetch telemetry (idle-wake audit E5/P2.12) ──────
// Everything below must work while the supabase client is WEDGED: no React (a
// paused tree may not re-render), no reportError (it awaits the wedged
// getSession), no supabase-js. Plain DOM + raw fetch only.

// The self-heal reload used to be fully silent — the operator's open dropdown
// just vanished (observed live in the audit's repro). Give the flash a cause.
function showReconnectingToast(): void {
  try {
    const el = document.createElement('div')
    el.setAttribute('role', 'status')
    el.textContent = 'Connection stuck — reloading to reconnect…'
    el.style.cssText =
      'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:2147483647;' +
      'background:#78350f;color:#fef3c7;padding:10px 16px;border-radius:12px;' +
      'font:600 13px system-ui,sans-serif;box-shadow:0 4px 24px rgba(0,0,0,.5)'
    document.body.appendChild(el)
  } catch { /* cosmetic */ }
}

// Terminal used to be SILENT: probing stopped and the operator was left with
// whatever wedged UI they had — the exact "only a manual reload fixes it"
// report. Tell them, with the one action that works.
function showTerminalBanner(): void {
  try {
    if (document.getElementById('kf49-wedge-terminal')) return
    const el = document.createElement('div')
    el.id = 'kf49-wedge-terminal'
    el.setAttribute('role', 'alert')
    el.style.cssText =
      'position:fixed;top:0;left:0;right:0;z-index:2147483647;background:#7f1d1d;color:#fee2e2;' +
      'padding:10px 14px;font:600 13px system-ui,sans-serif;display:flex;gap:12px;align-items:center;' +
      'justify-content:center;box-shadow:0 2px 16px rgba(0,0,0,.5)'
    const msg = document.createElement('span')
    msg.textContent = 'The app’s connection is stuck and automatic recovery has stopped — please reload.'
    const btn = document.createElement('button')
    btn.textContent = 'Reload'
    btn.style.cssText =
      'background:#fecaca;color:#7f1d1d;border:0;border-radius:8px;padding:6px 14px;' +
      'font:700 13px system-ui,sans-serif;cursor:pointer'
    btn.onclick = () => location.reload()
    el.append(msg, btn)
    document.body.appendChild(el)
  } catch { /* the crumb + console.warn still fire */ }
}

// The crumb flush only reaches client_errors on the NEXT boot — i.e. AFTER
// the manual reload the operator was just forced into, which is why terminal
// stops were invisible to ops until a day later. Ship it NOW with a raw fetch
// (global fetch, not the client's guardedFetch): the bearer comes straight
// from localStorage, bypassing the wedged getSession entirely. Best-effort —
// the next-boot crumb flush remains the reliable path.
function reportTerminalNow(context: Record<string, unknown>): void {
  try {
    const token = usableStoredToken()
    if (!token) return
    const base = import.meta.env.VITE_SUPABASE_URL as string
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string
    if (!base || !anon) return
    const sub = decodeStoredClaims()?.sub ?? null
    void fetch(`${base}/rest/v1/client_errors`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        apikey: anon,
        Authorization: `Bearer ${token}`,
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({
        user_id:     sub,
        source:      'manual',
        message:     'getSession wedge self-heal: terminal stop (live raw-fetch report)',
        url:         location.href,
        user_agent:  navigator.userAgent.slice(0, 500),
        app_version: import.meta.env.VITE_APP_VERSION ?? null,
        context,
      }),
    }).catch(() => { /* next-boot crumb flush covers it */ })
  } catch { /* best effort */ }
}

// Reload-or-give-up once the wedge is CONFIRMED. Split out of check() so the
// detection loop reads as a flat guard sequence and the irreversible action lives
// in one place. Guards re-checked here on purpose: the probe await before this can
// span seconds, during which online/token state may change.
// fallow-ignore-next-line complexity  (flat guard sequence; high CRAP is 0% coverage on a new module, not branching)
function recover(now: number): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  if (!usableStoredToken()) return
  const reloads = recentReloads(now)
  if (reloads.length >= MAX_RELOADS) {
    // Already reloaded MAX_RELOADS times in the window and the wedge keeps coming
    // back — the reload isn't fixing it. Stop; never loop the operator out.
    // LOUDLY (idle-wake audit E5): banner with a Reload button instead of the
    // old silent forever-wedge, and the telemetry ships NOW via raw fetch
    // instead of only after the operator's manual reload.
    stopped = true
    const crumb = { kind: 'getsession-wedge-terminal', path: location.pathname, ageMs: now - wedgeStart, reloads: reloads.length }
    queueCrumb(crumb)
    reportTerminalNow(crumb)
    showTerminalBanner()
    return
  }
  queueCrumb({ kind: 'getsession-wedge-reload', path: location.pathname, ageMs: now - wedgeStart, timeouts, reloads: reloads.length })
  try { sessionStorage.setItem(RELOADS_KEY, JSON.stringify([...reloads, now])) } catch { /* best effort */ }
  resetWedge()
  // A beat of pure-DOM context before the flash — the silent self-heal reload
  // read as "the app just ate my dropdown" (observed live in the audit).
  showReconnectingToast()
  setTimeout(() => location.reload(), 400)   // fresh JS context = no held in-process lock — the confirmed recovery
}

function recentReloads(now: number): number[] {
  try {
    return (JSON.parse(sessionStorage.getItem(RELOADS_KEY) || '[]') as number[])
      .filter(t => now - t < RELOAD_WINDOW_MS)
  } catch { return [] }
}

// One detection tick: a flat sequence of fail-closed guards, then probe, then the
// persistence gate, then recover(). Each guard is a documented safety check and the
// ORDER is load-bearing (cheap/synchronous skips before the async probe; re-checks
// before the irreversible reload), so this stays one readable top-down function
// rather than being scattered across helpers — same rationale as authRefresh.doRefresh.
// fallow-ignore-next-line complexity
async function check(): Promise<void> {
  if (stopped || killed() || onSessionlessKiosk()) return
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return // don't reload a backgrounded tab
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { resetWedge(); return }

  const stored = readStoredSession()
  if (!stored?.refresh_token) { resetWedge(); return }   // signed out → App.tsx routes to /login
  if (!usableStoredToken())   { resetWedge(); return }   // no live token → sessionWatchdog's job, not ours

  const wedged = await probeWedged()
  if (!wedged) { resetWedge(); return }                  // healthy, slow-but-working, or auth-js recovered

  // BOOT exclusion: a timeout before getSession has EVER resolved this
  // page-session is slow-start (fresh context, no held lock), not a wedge.
  if (!everResolved) return

  const now = Date.now()
  if (!wedgeStart) wedgeStart = now
  timeouts++

  // PERSISTENCE (two-tier): a wedge whose stored token is comfortably valid can't be
  // auth-js legitimately refreshing (it no-ops a fresh token), so confirm fast; near
  // expiry, stay past auth-js's ~30s bounded-recovery ceiling. Always need >= 2 probes.
  const secsToExpiry = (stored.expires_at ?? 0) - Math.floor(now / 1000)
  const confirmMs = secsToExpiry > FRESH_MARGIN_S ? FAST_CONFIRM_MS : SLOW_CONFIRM_MS
  if (now - wedgeStart < confirmMs || timeouts < 2) return

  recover(now)
}

export function installGetSessionHeal(): void {
  if (typeof window === 'undefined') return
  flushCrumbs()                       // ship any breadcrumb queued before a prior wedge-reload
  void check()                        // immediate probe so a healthy boot seeds everResolved at once
  setInterval(() => { void check() }, POLL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check()
  })
}
